import Vuex, { ModuleTree, Store } from "vuex";
import Vue from "vue";

import { ICommonState } from "./common/common-states.store";
import { ILookupState } from "./lookup/lookup-states.store";
import { IFavouriteFilterState } from "./favourite-filter/favourite-filter-states.store";
import { IUserState } from "./user/user-states.store";
import { IHistoryState } from "./history/history-states.store";
import { ITabViewState } from './tab-view/tab-view-types.store';
import {IFilterWrapperState} from './filter-wrapper/filter-wrapper-states.store';
import { IAppConfigState } from './config/app-config-types.store';
import { IKanbanState } from "./kanban/kanban-states.store";
import { IAIJobTrackerState } from "./ai-job-tracker/ai-job-tracker-states.store";

import { LookupModule } from "./lookup";
import { CommonModule } from "./common";
import { UserModule } from "./user";
import { HistoryModule } from "./history";
import { FavouriteFilterModule } from "./favourite-filter/favourite-filter-module";
import { TabViewModule } from './tab-view';
import { ModalModule } from './modal';
import {FilterWrapperModule} from './filter-wrapper';
import { AppConfigModule } from './config';
import { KanbanModule } from "./kanban";
import { AIJobTrackerModule } from "./ai-job-tracker";

import storeTypes from "./types";

import logger from './plugins/logger.plugin';
import { IModalState } from "./modal/modal-types.store";
import { AudioPlayerModule } from "./audio-player";
import { IAudioPlayerState } from "./audio-player/audio-player-types.store";

Vue.use(Vuex);

export interface IRootState {
	common?: ICommonState;
	lookup?: ILookupState;
	user?: IUserState;
	//report?: IReportState;
	history?: IHistoryState;
	favourites?: IFavouriteFilterState;
	tabView?: ITabViewState;
	filter?: IFilterWrapperState;
	modalState: IModalState;
	appConfig: IAppConfigState;
	kanban: IKanbanState;
	audioPlayer: IAudioPlayerState;
	aiJobTracker?: IAIJobTrackerState;
}

const store: Store<IRootState> = new Store<IRootState>({
	strict: true,
	modules: <ModuleTree<IRootState>>{
		common: new CommonModule<IRootState>(),
		lookup: new LookupModule<IRootState>(),
		user: new UserModule<IRootState>(),
		history: new HistoryModule<IRootState>(),
		favourites: new FavouriteFilterModule<IRootState>(),
		tabView: new TabViewModule<IRootState>(),
		filter: new FilterWrapperModule<IRootState>(),
		modal: new ModalModule<IRootState>(),
		appConfig: new AppConfigModule<IRootState>(),
		kanban: new KanbanModule<IRootState>(),
		audioPlayer: new AudioPlayerModule<IRootState>(),
		aiJobTracker: new AIJobTrackerModule<IRootState>()
	},
	plugins: process.env.NODE_ENV !== 'production' ? [logger] : []
});

export interface IBaseState<TService, TParam> {
	isPageRendered?: boolean;
	loadingList?: boolean;
	loadingDetail?: boolean;
	service?: TService;
	searchParam?: TParam;
	assignValue?: (
		obj: { value: { [key: string]: any }; destination: any }
	) => void;
}

const storeModules = {
	Common: "common",
	Agenda: "agenda",
	Lookup: "lookup",

	AgendaList: "agendaList",
	AgendaEdit: "agendaEdit",
	AgendaDetail: "agendaDetail",
	AgendaCommon: "agendaCommon",

	Site: "site",
	User: "user",
	Report: "report",
	History: "history",
	Favourites: "favourites",

	Person: "person-list",
	PersonConfiguration: "person-conf",
	PersonEdit: "person-edit",
	PersonDetail: "person-detail",
	Company: "company",

	Demand: "demand-list",
	DemandConfiguration: "demand-conf",
	DemandEdit: "demand-edit",
	DemandDetail: "demand-detail",

	Task: "task-list",
	TaskConfiguration: "task-conf",
	TaskEdit: "task-edit",
	TaskDetail: "task-detail",

	Property: "property",
	PropertyConfiguration: "property-conf",
	PropertyInit: "property-init",
	PropertyEdit: "property-edit",
	PropertyDetail: "property-detail",
	PropertyListView: "property-listview",

	ReportSendMessage: "report-send-message",
	ReportPrintTemplate: "report-print-template",
	ReportNotification: "report-notification",
	ReportLargeMailing: "report-large-mailing",

	PortalMedia: "portal",

	TabView: 'tabView',

	FilterWrapper : 'filter',

	ModalState: 'modalState',
	AppConfig: 'appConfig',
	Kanban: 'kanban',

	AudioPlayer: 'audioPlayer',
	AIJobTracker: 'aiJobTracker'
};

// Store modules whose `isSaving` flag blocks tab-switching / navigation.
const EDIT_STORE_MODULES = [
	storeModules.PersonEdit,
	storeModules.Company,
	storeModules.PropertyEdit,
	storeModules.TaskEdit,
	storeModules.DemandEdit,
	storeModules.AgendaEdit
];

export { store as default, storeModules, storeTypes, EDIT_STORE_MODULES };
